import { sameCompanyFaq } from "@/lib/brand";
import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryTarmac from "@/assets/sprinter-exec-orange.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryExec from "@/assets/sprinter-executive-2.jpg?format=webp&quality=72&w=1400&imagetools";
import gallerySuv from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/luxury-car-service-atlanta";
const PAGE_TITLE = "Luxury Car Service Atlanta | Private Car Service & VIP Chauffeurs";
const PAGE_DESC =
  "Luxury car service Atlanta trusts for celebrities, executives, and private jet travelers. Discreet private car service in Atlanta with NDA-bound chauffeurs. Call (678) 431-7222.";

const faqs = [
  {
    q: "What does luxury car service in Atlanta cost?",
    a: "Rates depend on vehicle, hours, and route. Mercedes-Benz Sprinter Limousine service starts from $250/hr, and custom quotes are given upon request. Airport and FBO transfers are quoted as flat rates before you book.",
  },
  hourlyMinimumFaq(),
  sameCompanyFaq(),
  {
    q: "How is private car service different from a black car app?",
    a: "A private car service assigns a specific chauffeur and a specific vehicle to your itinerary, in advance. Apps assign whoever is nearby. For a principal with a schedule, a security detail, or a wheels-up time, that difference is the entire service.",
  },
  {
    q: "Do your chauffeurs sign NDAs?",
    a: "Yes. Every chauffeur working celebrity, athlete, executive, and production accounts is background-checked and NDA-bound. Manifests are kept internal, itineraries are never discussed outside our team, and we do not photograph or post clients or vehicles in service.",
  },
  {
    q: "Can you provide privacy glass and low-profile arrivals?",
    a: "Our Sprinters and Suburbans run privacy glass in the rear cabin. We stage at service entrances, loading docks, and FBO ramps rather than main lobbies, and we coordinate with venue security or your protection team on approach, hold points, and departure routing.",
  },
  {
    q: "Do you work with executive assistants and corporate travel managers?",
    a: "Constantly. Roughly half our corporate volume is booked by EAs, travel managers, and flight departments. We work from your itinerary document, confirm each leg in writing, and send day-of updates to the coordinator rather than interrupting the principal.",
  },
  {
    q: "Can you handle multi-vehicle roadshows and executive groups?",
    a: "Yes. Multi-vehicle programs are staged with a single point of contact, synchronized departures, and one consolidated itinerary. Board meetings, investor roadshows, and leadership offsites across Buckhead, Midtown, Perimeter, and Alpharetta are routine work for us.",
  },
  {
    q: "Do you meet private jets at Atlanta FBOs?",
    a: "We do. Planeside pickups are standard at PDK, Fulton County (FTY), Cobb County (RYY), Gwinnett (LZU), and the ATL private terminal, with tail-number flight tracking so the vehicle is staged before the door opens. See our private jet and FBO pages for terminal-level detail.",
  },
  {
    q: "How far in advance should a VIP booking be placed?",
    a: "24–48 hours is comfortable for standard executive transfers. For protection details, multi-vehicle programs, and peak weeks, Masters, Super Bowl, Final Four, awards and festival dates, book two to four weeks out. Same-day requests are worth a call; we hold reserve capacity.",
  },
  {
    q: "What vehicles are available for luxury car service?",
    a: "Mercedes-Benz Sprinter Limousines for groups up to 14, Executive Sprinters with captain's chairs and work tables for corporate teams, and Suburban SUVs for solo principals, couples, and security-forward moves. Every vehicle is detailed before every trip.",
  },
];

export const Route = createFileRoute("/luxury-car-service-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "luxury car service atlanta, private car service atlanta, VIP car service Atlanta, celebrity car service Atlanta, executive car service Atlanta, corporate car service Atlanta, private jet car service Atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Luxury Car Service Atlanta",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Luxury Car Service & Private Car Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Midtown Atlanta" },
              { type: "City", name: "Sandy Springs" },
              { type: "City", name: "Alpharetta" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
              { type: "Airport", name: "Fulton County Airport, Brown Field (FTY)" },
            ],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • Luxury & Private Car Service"
      h1="Luxury Car Service Atlanta"
      intro="You may be landing after a long week or welcoming a client to Atlanta. My family provides private, professional chauffeured travel in Mercedes-Benz Sprinters and Suburban SUVs, with every detail handled discreetly."
      heroImage={heroImage}
      heroAlt="Luxury car service Atlanta, chauffeured Mercedes-Benz Sprinter staged for a VIP client"
      midImage={midImage}
      midAlt="Private car service Atlanta, Mercedes-Benz Sprinter Limousine lounge cabin with privacy glass"
      sectionOneTitle="Travel With Your Privacy in Mind"
      sectionOneBody={[
        "Your itinerary stays personal. My chauffeurs handle executive, celebrity and private-aviation travel with discretion.",
        "Atlanta Luxury Sprinters is the luxury Sprinter fleet of Empress Elite Limousine, my family's company, rated 5.0 on Google with 338 reviews.",
        "Choose a Mercedes-Benz Sprinter for your group or a Suburban SUV for calm executive, family and airport travel. We own every vehicle in our fleet.",
      ]}
      bulletsTitle="What Our VIP Clients Rely On"
      bulletsIntro="Careful preparation, professional chauffeurs and a private cabin."
      bullets={[
        "NDA-bound, background-checked chauffeurs on every VIP account",
        "Privacy glass and discreet, non-lobby staging",
        "Tail-number flight tracking for private aviation arrivals",
        "Coordination with security details, EAs, and flight departments",
        "Multi-vehicle programs run from one point of contact",
        "24/7 support answered by a person, not a queue",
      ]}
      coverageTitle="Where We Provide Luxury Car Service"
      coverageIntro="Door-to-door private car service across metro Atlanta, its venues, hotels, and every private aviation terminal."
      coverage={[
        "Buckhead",
        "Midtown Atlanta",
        "Downtown Atlanta",
        "Sandy Springs",
        "Alpharetta",
        "Perimeter & Dunwoody",
        "Vinings & Smyrna",
        "Brookhaven",
        "Marietta",
        "Decatur",
        "Serenbe & Chateau Elan",
        "ATL, PDK, FTY, RYY & LZU",
      ]}
      detailSections={[
        {
          title: "Celebrities, Athletes & Public Figures",
          body: [
            "Atlanta runs on film production, touring, and professional sport, and the ground transportation for those clients is judged on one thing: whether anything about the movement became public.",
            "Chauffeurs on these accounts are NDA-bound and briefed only on what the run requires. Vehicles carry privacy glass in the rear cabin and no branding. We stage at service entrances, ramps, and loading docks, hold out of sightlines, and depart on the protection team's call rather than the venue's.",
          ],
          list: [
            "NDA-bound chauffeurs; itineraries held inside our team only.",
            "Unbranded vehicles with rear privacy glass.",
            "Service-entrance and ramp-side staging, never a main lobby unless requested.",
            "Coordination with personal security, tour managers, and production transport leads.",
            "Multi-vehicle convoys for talent, glam, and crew moving on one schedule.",
            "No photography, no social posting, no client names on any manifest we share.",
          ],
        },
        {
          title: "High-End Corporate & Executive Clients",
          body: [
            "For C-suite travel the failure mode is never comfort, it is a schedule that slips. Our corporate programs are built around the person who owns the calendar, whether that is the executive, the assistant, or the flight department.",
          ],
          list: [
            "Executive assistants book once and receive written confirmation of every leg.",
            "Hourly and as-directed days so the chauffeur and vehicle stay with the principal.",
            "Investor roadshows and board weeks with staged, synchronized multi-vehicle departures.",
            "Client and board-member pickups handled to the same standard as the principal's.",
            "Certificates of insurance available for corporate and venue vendor requirements.",
            "Wi-Fi, work tables, and quiet cabins on Executive Sprinter bookings.",
          ],
        },
        {
          title: "Private Jet Travelers",
          body: [
            "Private aviation clients are the reason our team tracks tail numbers rather than clock times. The vehicle is staged before wheels-down, and the transfer from aircraft door to cabin door is measured in steps, not minutes.",
            "We cover PDK, Fulton County (FTY), Cobb County (RYY), Gwinnett (LZU), and the ATL private terminal, plus every private field in the state. Terminal-by-terminal detail lives on our aviation pages rather than being repeated here.",
          ],
          list: [
            "Planeside pickup where the FBO permits ramp access.",
            "Tail-number tracking with automatic adjustment for early or delayed arrivals.",
            "Signature, Atlantic Aviation, Million Air, and Epps Aviation protocols already on file.",
            "Luggage, golf bags, and equipment handled by the chauffeur, not the passenger.",
            "Full statewide coverage detailed on our Georgia private jet airports page.",
          ],
        },
      ]}
      gallery={{
        title: "The Vehicles Behind the Service",
        intro: "Mercedes-Benz Sprinters and Suburban SUVs, detailed before every trip.",
        images: [
          {
            src: galleryTarmac,
            alt: "Luxury car service Atlanta, Executive Sprinter cabin with captain's chairs",
            caption: "Planeside staging, private aviation arrivals",
          },
          {
            src: galleryExec,
            alt: "Executive Mercedes-Benz Sprinter for Atlanta corporate car service",
            caption: "Executive Sprinter, corporate and roadshow days",
          },
          {
            src: gallerySuv,
            alt: "Suburban SUV interior for Atlanta private car service",
            caption: "Suburban SUV, solo principals and security-forward moves",
          },
        ],
      }}
      faqTitle="Luxury & Private Car Service FAQs"
      relatedTitle="Related Services"
      faqs={faqs}
      related={[
        {
          to: "/private-jet-airports-georgia",
          title: "Georgia Private Jet Airports",
          desc: "VIP chauffeur coverage at every private aviation terminal in Georgia.",
        },
        {
          to: "/chauffeur-service-atlanta",
          title: "Private Chauffeur Service Atlanta",
          desc: "Hourly, as-directed, and multi-day chauffeur programs across metro Atlanta.",
        },
        {
          to: "/atlanta-airport-car-service",
          title: "Atlanta Airport Car Service",
          desc: "Flat-rate luxury transfers to and from Hartsfield-Jackson.",
        },
      ]}
      ctaTitle="Text Me Your Travel Plans"
      ctaBody="Send me your dates, pickup and itinerary or tail number. I'll check the vehicle and arrangements personally, then send your quote in writing."
    />
  );
}
